import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IonContent, IonPage } from "@ionic/react";
import GoogleMapReact from "google-map-react";
import { CircularProgress, TextField } from "@mui/material";
import { ArrowLeft, Navigation } from "lucide-react";
import { MapPinIcon as MapPinSolidIcon } from "@heroicons/react/24/solid";
import { useSelector } from "react-redux";
import {
  getCurrentLocation,
  userMessageForLocationError,
} from "../../../services/LocationService";
import utilityService from "../../../services/UtilityService";
import addressService from "../../../services/AddressService";
import familyPlanServices from "../../../services/FamilyPlanServices";
import Pin from "../../../components/common/location/Pin";
import useUnhidePageOnEnter from "../../../hooks/useUnhidePageOnEnter";
import { goBackTo } from "../../navigo/utils/navBack";
import { isPBSession } from "../../../utils/appSource";

// Until we have a location, show the whole country rather than a city we
// guessed at: zoom 4 around India's centroid fits ~8-37N / 68-97E on a
// phone-sized map div.
const DEFAULT_CENTER = { lat: 22.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 4;
const LOCATED_ZOOM = 16;

const FORM_FIELD_SX = {
  "& .MuiInputBase-input": {
    fontFamily: "'Montserrat', sans-serif",
    color: "#12194C",
  },
  "& .MuiInputBase-root": { fontFamily: "'Montserrat', sans-serif" },
  "& .MuiInputLabel-root": {
    fontFamily: "'Montserrat', sans-serif",
    color: "#9CA3AF",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "#374151" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#374151" },
  "& .MuiFormLabel-asterisk, & .MuiInputLabel-asterisk": { color: "#DC2626" },
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Montserrat', sans-serif",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#D1D5DB" },
    "&.Mui-focused fieldset": { borderColor: "#9CA3AF" },
  },
};

const ADDRESS_TYPES = ["Home", "Work", "Other"];

const initialForm = {
  name: "",
  phone: "",
  housing: "",
  colony: "",
  landmark: "",
  pincode: "",
  city: "",
  district: "",
  state: "",
  addressType: "Home",
};

async function reverseGeocode(lat, lng) {
  try {
    const res = await utilityService.getFormattedAddressFromCoordinates({
      latitude: lat,
      longitude: lng,
    });
    return res?.data || null;
  } catch {
    return null;
  }
}

function addressNeedsCoordinatesUpdate(addr) {
  const c = addr?.coordinates;
  if (!c) return true;
  if (c.latitude === 0 && c.longitude === 0) return true;
  return false;
}

function formatAddressLine(d) {
  if (!d) return "";
  const parts = [
    d.housing,
    d.building,
    d.street,
    d.landmark,
    d.subLocality,
    d.localityArea,
  ]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);
  let line = parts.join(", ");
  const pincode =
    d.pincode != null && String(d.pincode).trim() !== ""
      ? String(d.pincode).trim()
      : "";
  if (pincode) line = (line ? line + " " : "") + "-" + pincode;
  return line;
}

// Map + form screen to add a new address or edit an existing one for the
// standalone Address book. Saved-address shape matches the booking flow.
export default function ManageAddress() {
  // Ionic can leave this page `ion-page-hidden` on re-entry, blanking it.
  const pageRef = useUnhidePageOnEnter();
  const history = useHistory();
  const routeLocation = useLocation();
  const editAddress = routeLocation?.state?.editAddress || null;
  const isEditing = Boolean(editAddress?.id);

  const patientProfile = useSelector(
    (store) => store.login.user.patientProfile
  );
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  // PB does not get the geolocation features in this release, so it starts on
  // the typed form and the "pin" step — the only thing that renders a map on
  // this screen — is never reached. Geolocation for PB is a separate release.
  //
  // Safe without a location: coordinates are not required to save an address.
  // (0,0) is the marker the app already uses for "needs resolving", and it
  // backfills them from the stored address via
  // addressService.getCoordinatesFromAddress(addressId).
  const hideLocationFeatures = isPBSession();
  const [mode, setMode] = useState(hideLocationFeatures ? "form" : "pin"); // "pin" | "form"
  const [coords, setCoords] = useState(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [addressData, setAddressData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [isLocating, setIsLocating] = useState(false);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const startedRef = useRef(false);
  const mapRef = useRef(null);
  const mapsApiRef = useRef(null);
  const hasFitRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  const handleMapApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsApiRef.current = maps;
    setMapReady(true);
  };

  // Center the map on the pin imperatively — google-map-react doesn't reliably
  // re-apply the controlled center/zoom (so the edit pin landed off-screen).
  // First fit jumps instantly (avoids a long pan from the default center);
  // every later pin drop animates via panTo.
  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps || !coords) return;
    const isFirstFit = !hasFitRef.current;
    const applyView = () => {
      if (isFirstFit) {
        if (typeof map.setZoom === "function") map.setZoom(LOCATED_ZOOM);
        if (typeof map.setCenter === "function") map.setCenter(coords);
      } else if (typeof map.panTo === "function") {
        map.panTo(coords);
      } else if (typeof map.setCenter === "function") {
        map.setCenter(coords);
      }
    };
    applyView();
    hasFitRef.current = true;
    if (maps?.event && typeof maps.event.addListenerOnce === "function") {
      maps.event.addListenerOnce(map, "idle", applyView);
    }
  }, [coords, mapReady]);

  const dropPinAt = async (lat, lng) => {
    const next = { lat, lng };
    // Don't push center/zoom into the controlled props here — that snaps the map
    // instantly. The imperative effect below animates the move with panTo.
    setCoords(next);
    setIsFetchingAddress(true);
    try {
      const formatted = await reverseGeocode(lat, lng);
      if (formatted) setAddressData(formatted);
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const locateCurrent = async () => {
    setError(null);
    setIsLocating(true);
    try {
      const result = await getCurrentLocation({
        patientId: patientProfile?.id,
      });
      await dropPinAt(result.lat, result.lng);
    } catch (err) {
      setError(userMessageForLocationError(err));
    } finally {
      setIsLocating(false);
    }
  };

  // On entry: prefill (edit) and position the pin (edit coords or current
  // location for a new address).
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (isEditing) {
      setForm({
        name: editAddress.name || "",
        phone: editAddress.phoneNo != null ? String(editAddress.phoneNo) : "",
        housing: editAddress.line1 || "",
        colony: editAddress.line2 || "",
        landmark: editAddress.landmark || "",
        pincode: editAddress.pincode != null ? String(editAddress.pincode) : "",
        city: editAddress.city || "",
        district: editAddress.district || "",
        state: editAddress.state || "",
        addressType: editAddress.type || "Home",
      });
      if (!addressNeedsCoordinatesUpdate(editAddress)) {
        dropPinAt(
          editAddress.coordinates.latitude,
          editAddress.coordinates.longitude
        );
      } else {
        addressService
          .getCoordinatesFromAddress(editAddress.id)
          .then((res) => {
            const lat = res?.data?.latitude;
            const lng = res?.data?.longitude;
            if (typeof lat === "number" && typeof lng === "number") {
              dropPinAt(lat, lng);
            } else {
              locateCurrent();
            }
          })
          .catch(() => locateCurrent());
      }
    } else {
      locateCurrent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMapClick = ({ lat, lng }) => {
    dropPinAt(lat, lng);
  };

  const updateForm = (field, value) => {
    // The banner describes the payload the server rejected, so it is stale as
    // soon as anything changes — and it was occupying space above the keyboard.
    setSaveError(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEnterFullAddress = () => {
    // Prefill from the reverse-geocoded pin only for a new address; keep the
    // user's saved values when editing.
    if (!isEditing) {
      const fullName =
        patientProfile?.fullName ||
        [patientProfile?.firstName, patientProfile?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
      setForm({
        name: fullName || "",
        phone: patientProfile?.phoneno ? String(patientProfile.phoneno) : "",
        housing: addressData?.housing || addressData?.building || "",
        colony:
          addressData?.street ||
          addressData?.subLocality ||
          addressData?.localityArea ||
          "",
        landmark: addressData?.landmark || "",
        pincode:
          addressData?.pincode != null ? String(addressData.pincode) : "",
        city: addressData?.city || "",
        district: addressData?.district || "",
        state: addressData?.state || "",
        addressType: "Home",
      });
    }
    setMode("form");
  };

  const isFormValid = Boolean(
    form.name.trim() &&
      form.phone.trim() &&
      form.housing.trim() &&
      form.colony.trim() &&
      form.pincode.trim()
  );

  const handleSave = async () => {
    if (isSaving || !isFormValid) return;
    if (!coords && !hideLocationFeatures) return;
    setSaveError(null);
    setIsSaving(true);

    const body = {
      name: form.name.trim(),
      phoneNo: parseInt(form.phone.replace(/\D/g, ""), 10),
      countryCode: 91,
      line1: form.housing.trim(),
      line2: form.colony.trim(),
      landmark: form.landmark.trim(),
      district: form.district.trim(),
      state: form.state.trim(),
      city: form.city.trim(),
      pincode: parseInt(form.pincode.replace(/\D/g, ""), 10),
      patientId: patientProfile?.id,
      isDefault: false,
      type: form.addressType,
      // (0,0) when there is no location — the PB path; resolved later from
      // the address text rather than saved to a wrong position.
      coordinates: {
        latitude: coords?.lat ?? 0,
        longitude: coords?.lng ?? 0,
      },
    };

    try {
      if (isEditing) {
        await addressService.updateAddress(editAddress.id, body);
      } else {
        await familyPlanServices.postAddress(body, false);
      }
      goBackTo(history, routeLocation, "/address");
    } catch (err) {
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          "Couldn't save address. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isSaving) return;
    if (mode === "form" && !hideLocationFeatures) {
      setMode("pin");
    } else {
      goBackTo(history, routeLocation, "/address");
    }
  };

  return (
    <IonPage ref={pageRef}>
      <IonContent className="no-keyboard-offset">
        <div
          style={{
            fontFamily: "'Montserrat', sans-serif",
            backgroundColor: "#FFFFFF",
            // Plain block for the form step, like this app's other forms — see
            // the same change in navigo/components/selectLocation.js. The map
            // step keeps its viewport-height column so the map can fill it.
            ...(mode === "pin"
              ? { height: "100%", display: "flex", flexDirection: "column" }
              : {}),
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "16px",
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <button
              type="button"
              onClick={handleBack}
              aria-label="Back"
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#97144D",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ArrowLeft size={22} />
            </button>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#12194C",
              }}
            >
              {isEditing ? "Edit address" : "Add address"}
            </h2>
          </div>

          {mode === "pin" ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  margin: "12px 16px",
                  borderRadius: 12,
                  overflow: "hidden",
                  border: "1px solid #E5E7EB",
                }}
              >
                {!apiKey ? (
                  <div
                    style={{
                      padding: 16,
                      color: "#6B7280",
                      textAlign: "center",
                    }}
                  >
                    Google Maps API key missing.
                  </div>
                ) : (
                  <GoogleMapReact
                    bootstrapURLKeys={{ key: apiKey }}
                    center={center}
                    zoom={zoom}
                    onClick={handleMapClick}
                    onChange={({ center: c, zoom: z }) => {
                      if (c) setCenter(c);
                      if (typeof z === "number") setZoom(z);
                    }}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={handleMapApiLoaded}
                    options={{
                      fullscreenControl: false,
                      mapTypeControl: false,
                      streetViewControl: false,
                      zoomControl: true,
                    }}
                  >
                    {coords && <Pin lat={coords.lat} lng={coords.lng} />}
                  </GoogleMapReact>
                )}

                <button
                  type="button"
                  onClick={locateCurrent}
                  disabled={isLocating}
                  style={{
                    position: "absolute",
                    bottom: 12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 18px",
                    backgroundColor: "#97144D",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 999,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: isLocating ? "default" : "pointer",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {isLocating ? (
                    <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    <Navigation size={16} />
                  )}
                  Current location
                </button>
              </div>

              {error && (
                <p
                  style={{
                    margin: "0 16px 8px",
                    fontSize: 13,
                    color: "#B91C1C",
                  }}
                >
                  {error}
                </p>
              )}

              <div style={{ padding: 16, borderTop: "1px solid #F1F5F9" }}>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    marginBottom: 14,
                  }}
                >
                  <MapPinSolidIcon
                    style={{
                      width: 20,
                      height: 20,
                      color: "#97144D",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  />
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: 1.45,
                    }}
                  >
                    {isFetchingAddress
                      ? "Fetching location…"
                      : formatAddressLine(addressData) ||
                        "Move the map to set your location"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleEnterFullAddress}
                  disabled={!coords}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    backgroundColor: coords ? "#97144D" : "#D1D5DB",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: coords ? "pointer" : "not-allowed",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Enter full address
                </button>
              </div>
            </div>
          ) : (
            <div>
              {!hideLocationFeatures && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "14px 16px",
                  borderBottom: "1px solid #F1F5F9",
                }}
              >
                <MapPinSolidIcon
                  style={{
                    width: 20,
                    height: 20,
                    color: "#97144D",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: "#374151",
                    lineHeight: 1.45,
                  }}
                >
                  {isFetchingAddress
                    ? "Fetching location…"
                    : [
                        addressData?.housing,
                        addressData?.street,
                        addressData?.subLocality,
                        addressData?.localityArea,
                        addressData?.city,
                      ]
                        .filter(Boolean)
                        .join(", ") || "Pinned location"}
                </p>
              </div>
              )}

              <div
                style={{
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                <TextField
                  label="Full name"
                  required
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.name}
                  onChange={(e) => updateForm("name", e.target.value)}
                />
                <TextField
                  label="Phone number"
                  required
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.phone}
                  onChange={(e) => updateForm("phone", e.target.value)}
                />
                <TextField
                  label="House / Flat / Building"
                  required
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.housing}
                  onChange={(e) => updateForm("housing", e.target.value)}
                />
                <TextField
                  label="Colony / Area / Street"
                  required
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.colony}
                  onChange={(e) => updateForm("colony", e.target.value)}
                />
                <TextField
                  label="Landmark"
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.landmark}
                  onChange={(e) => updateForm("landmark", e.target.value)}
                />
                <TextField
                  label="Pincode"
                  required
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.pincode}
                  onChange={(e) => updateForm("pincode", e.target.value)}
                />
                <div style={{ display: "flex", gap: 12 }}>
                  <TextField
                    label="City"
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    value={form.city}
                    onChange={(e) => updateForm("city", e.target.value)}
                  />
                  <TextField
                    label="District"
                    size="small"
                    fullWidth
                    sx={FORM_FIELD_SX}
                    value={form.district}
                    onChange={(e) => updateForm("district", e.target.value)}
                  />
                </div>
                <TextField
                  label="State"
                  size="small"
                  fullWidth
                  sx={FORM_FIELD_SX}
                  value={form.state}
                  onChange={(e) => updateForm("state", e.target.value)}
                />

                <div style={{ display: "flex", gap: 10 }}>
                  {ADDRESS_TYPES.map((t) => {
                    const active = form.addressType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => updateForm("addressType", t)}
                        style={{
                          flex: 1,
                          padding: "10px 0",
                          borderRadius: 8,
                          border: `1px solid ${active ? "#97144D" : "#E5E7EB"}`,
                          backgroundColor: active ? "#FBE7ED" : "#FFFFFF",
                          color: active ? "#97144D" : "#6B7280",
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "'Montserrat', sans-serif",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div
                style={{
                  // Plain in-flow bar at the end of the form. Not sticky and not
                  // fixed: anything pinned here floats over the fields the moment
                  // the viewport changes (focusing a field, keyboard opening).
                  padding:
                    "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
                  borderTop: "1px solid #F1F5F9",
                }}
              >
                {saveError && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: "10px 12px",
                      fontSize: 13,
                      color: "#B91C1C",
                      backgroundColor: "#FEF2F2",
                      border: "1px solid #FCA5A5",
                      borderRadius: 6,
                    }}
                  >
                    {saveError}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isFormValid || isSaving}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    backgroundColor:
                      isFormValid && !isSaving ? "#97144D" : "#D1D5DB",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor:
                      isFormValid && !isSaving ? "pointer" : "not-allowed",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {isSaving
                    ? "Saving…"
                    : isEditing
                    ? "Update address"
                    : "Save address"}
                </button>
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
